import React, { useEffect, useState, useRef } from "react";
import { X, Banknote, ShieldCheck, AlertTriangle, Loader2 } from "lucide-react";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import { ConnectAccountOnboarding, ConnectComponentsProvider } from "@stripe/react-connect-js";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Stripe Connect Express EMBEDDED onboarding.
 * Renders Stripe's iframe inside our modal — no redirect to stripe.com.
 *
 * Props:
 *  - open: boolean
 *  - onClose() — dismiss
 *  - onComplete() — fired when Stripe says onboarding is finished
 */
export default function ConnectOnboardingModal({ open, onClose, onComplete }) {
  const [stripeConnect, setStripeConnect] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const initRef = useRef(false);

  useEffect(() => {
    if (!open || initRef.current) return;
    initRef.current = true;
    setLoading(true);
    setError(null);

    const fetchClientSecret = async () => {
      const { client_secret } = await api.connectOnboard();
      return client_secret;
    };

    // Initialize stripe-connect
    (async () => {
      try {
        const cfg = await api.connectPublishableKey();
        const sc = await loadConnectAndInitialize({
          publishableKey: cfg.publishable_key,
          fetchClientSecret,
          appearance: {
            overlays: "dialog",
            variables: {
              colorPrimary: "#0A0A0B",
              colorBackground: "#FFFFFF",
              fontFamily: "Inter, system-ui, sans-serif",
              borderRadius: "0px",
            },
          },
        });
        setStripeConnect(sc);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Failed to load Stripe Connect");
        toast.error("Could not start Stripe onboarding");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  // Reset when closed so reopening starts fresh
  useEffect(() => {
    if (!open) {
      initRef.current = false;
      setStripeConnect(null);
      setError(null);
    }
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="connect-onboarding-modal">
      <div className="bg-white border-2 border-black max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-black bg-[#FAFAFA] sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <Banknote size={18} />
            <div className="text-eyebrow">Connect your bank · powered by Stripe</div>
          </div>
          <button onClick={onClose} className="hover:text-[#E11D48]" data-testid="connect-modal-close"><X size={18}/></button>
        </div>

        <div className="p-6">
          <div className="border border-black p-4 mb-5 bg-[#FFFBEB] flex items-start gap-3">
            <ShieldCheck size={18} className="text-[#B45309] shrink-0 mt-0.5"/>
            <div className="text-sm">
              <div className="font-semibold mb-1">Why we use Stripe</div>
              <div className="text-[#52525B] leading-relaxed text-xs">Stripe handles all the bank/tax verification (KYC). We never see your bank details — they're stored at Stripe directly. Once verified, every commission ships automatically to your bank.</div>
            </div>
          </div>

          {loading && (
            <div className="flex items-center gap-3 text-sm text-[#52525B] py-12 justify-center">
              <Loader2 size={16} className="animate-spin"/> Loading Stripe…
            </div>
          )}

          {error && (
            <div className="border border-[#E11D48] bg-[#FEE2E2] p-4 text-sm flex items-start gap-3">
              <AlertTriangle size={16} className="text-[#E11D48] shrink-0 mt-0.5"/>
              <div>
                <div className="font-semibold">Could not start onboarding</div>
                <div className="text-xs mt-1">{error}</div>
              </div>
            </div>
          )}

          {stripeConnect && (
            <ConnectComponentsProvider connectInstance={stripeConnect}>
              <ConnectAccountOnboarding
                onExit={() => {
                  toast.message("Closed onboarding — you can resume any time.");
                  onComplete && onComplete();
                  onClose();
                }}
              />
            </ConnectComponentsProvider>
          )}
        </div>

        <div className="border-t border-black p-4 bg-[#FAFAFA] text-xs text-[#52525B] flex items-center justify-between">
          <span>Your data is sent directly to Stripe over an encrypted channel.</span>
          <button onClick={onClose} className="text-eyebrow hover:text-black" data-testid="connect-modal-cancel">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
